import React, { useMemo, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// === Colors (KRAJ, UE, IMPORT) ===
const COLORS = {
  KRAJ: "#9848c7",     // purple
  UE: "#e53935",       // red
  IMPORT: "#d1b3ff",  // light purple
};

const EU_CODES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PT","RO","SK","SI","ES","SE" // PL handled separately as KRAJ
]);

const safeParse = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const formatNumber = (value) =>
  Number(value).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const months = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const ZakupyStructureOverTime = ({ data = [] }) => {
  const years = useMemo(() => {
    const set = new Set();
    data.forEach(row => {
      const d = new Date(row.DataZakupu);
      if (!isNaN(d)) set.add(d.getFullYear());
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [data]);

  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    if (years.length && !selectedYear) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const monthlyData = useMemo(() => {
    const totals = Array(12).fill(0).map(() => ({
      KRAJ: 0,
      UE: 0,
      IMPORT: 0
    }));

    data.forEach(row => {
      const date = new Date(row.DataZakupu);
      if (isNaN(date)) return;
      const month = date.getMonth();
      const year = date.getFullYear();
      if (year !== selectedYear) return;

      const kraj = (row.KodKrajuNadaniaTIN || "").trim().toUpperCase();
      const kwota = safeParse(row.K_42) + safeParse(row.K_43);

      if (kraj === "PL") {
        totals[month].KRAJ += kwota;
      } else if (kraj && EU_CODES.has(kraj)) {
        totals[month].UE += kwota;
      } else if (kraj) {
        totals[month].IMPORT += kwota; // poza UE
      }
      // Brak kraju -> pomijamy
    });

    return totals;
  }, [data, selectedYear]);

  const chartData = {
    labels: months,
    datasets: [
      {
        label: "KRAJ",
        data: monthlyData.map(m => Number(m.KRAJ.toFixed(2))),
        backgroundColor: COLORS.KRAJ
      },
      {
        label: "UE",
        data: monthlyData.map(m => Number(m.UE.toFixed(2))),
        backgroundColor: COLORS.UE
      },
      {
        label: "IMPORT",
        data: monthlyData.map(m => Number(m.IMPORT.toFixed(2))),
        backgroundColor: COLORS.IMPORT
      }
    ]
  };

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Zakupy wg pochodzenia transakcji w czasie
        <InfoIconWrapper data-tooltip-id="zakupy-structure-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="zakupy-structure-tooltip"
        place="right"
        content={
          <>
            <strong>Pochodzenie transakcji</strong><br/>
            Miesięczny podział wartości zakupów na: KRAJ, UE, IMPORT.
          </>
        }
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "white",
          backgroundColor: "#333"
        }}
      />

      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "center", gap: "12px", alignItems: "center" }}>
        <label>Rok:</label>
        <select
          value={selectedYear || ""}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          style={{
            padding: "8px 12px",
            fontSize: "15px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      <div style={{ width: "100%", maxWidth: "900px", height: "580px", margin: "0 auto" }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${formatNumber(context.raw)} zł`
                },
              },
            },
            scales: {
              x: { stacked: true },
              y: {
                stacked: true,
                title: {
                  display: true,
                  text: "Kwota",
                  color: "#666",
                  font: { size: 12, weight: "bold" }
                },
                ticks: {
                  callback: (value) => `${formatNumber(value)} zł`
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default ZakupyStructureOverTime;
